import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUser, FiCheck, FiClock, FiDollarSign, FiAward, FiChevronDown, FiExternalLink } from 'react-icons/fi';
import axios from 'axios';
import './ReferralGenerator.css';
import { useNavigate } from 'react-router-dom';

const TrackLeads = () => {
  const [vpa, setVpa] = useState('');
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);
  const [offers, setOffers] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.post('https://offers.cashbolt.in.net/server/', {
          action: 'getActiveOffers'
        });

        if (response.data.status) {
          setOffers(response.data.data);
          if (response.data.data.length > 0) {
            setSelectedOffer(response.data.data[0]);
          }
        } else {
          throw new Error(response.data.message || 'Failed to fetch offers');
        }
      } catch (err) {
        console.error("Error fetching offers:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, []);


  const fetchEvents = async () => {
    if (!vpa) {
      setError('Please enter a VPA');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Mock API call - replace with your actual endpoint
      const response = await axios.post('https://offers.cashbolt.in.net/server/', {
        action: 'getLeadEvents',
        vpa: vpa,
        offerId: selectedOffer.offerId
      });

      setEvents(response.data); // Replace with response.data in production
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'install': return <FiUser className="event-icon" />;
      case 'register': return <FiCheck className="event-icon" />;
      case 'deposit': return <FiDollarSign className="event-icon" />;
      case 'first_bet': return <FiAward className="event-icon" />;
      default: return <FiUser className="event-icon" />;
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-pulse"></div>
      </div>
    );
  }

  return (
    <div className="referral-page">
      {/* Animated background elements */}
      <motion.div
        className="bg-shine"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.1 }}
        transition={{ duration: 1.5 }}
      />

      {/* Main container */}
      <motion.div
        className="referral-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Page header */}
        <div className="referral-header">
          <motion.div
            className="referral-icon"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <FiUser />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h1>Track Lead Events</h1>
            <p className="referral-subtitle">Monitor conversions and earnings</p>
          </motion.div>
        </div>

        <AnimatePresence>
          {!events ? (
            <motion.div
              key="input-form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="custom-select-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <div
                  className="custom-select-header"
                  onClick={() => setIsOpen(!isOpen)}
                >
                  {selectedOffer && (
                    <div className="selected-offer">
                      <div
                        className="offer-logo"
                        style={{ backgroundColor: selectedOffer.color }}
                      >
                        {selectedOffer.name.charAt(0)}
                      </div>
                      <div className="offer-details">
                        <h3>{selectedOffer.name}</h3>
                        <p>₹{selectedOffer.userEventsAmount.split(',').slice(-1)[0]} on completion</p>
                      </div>
                    </div>
                  )}
                  <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FiChevronDown />
                  </motion.div>
                </div>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      className="custom-select-options"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {offers.map((offer) => (
                        <div
                          key={offer.offerId}
                          className={`offer-option ${selectedOffer?.offerId === offer.offerId ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedOffer(offer);
                            setIsOpen(false);
                          }}
                        >
                          <div
                            className="offer-logo"
                            style={{ backgroundColor: offer.color }}
                          >
                            {offer.name.charAt(0)}
                          </div>
                          <div className="offer-details">
                            <h3>{offer.name}</h3>
                            <p>₹{offer.userEventsAmount.split(',').slice(-1)[0]} on completion</p>
                          </div>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
              {/* VPA input */}
              <motion.div
                className="input-wrapper"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <div className="input-container">
                  <label htmlFor="vpa">Your Votex VPA</label>
                  <motion.div
                    className="input-field"
                    whileHover={{ y: -2 }}
                  >
                    <input
                      type="text"
                      id="vpa"
                      value={vpa}
                      onChange={(e) => setVpa(e.target.value)}
                      placeholder="yourname@votex"
                      required
                    />
                  </motion.div>
                </div>
              </motion.div>

              {error && <div className="error-message">{error}</div>}

              {/* Track button */}
              <motion.button
                className="generate-btn"
                onClick={fetchEvents}
                disabled={!vpa || !selectedOffer}
                whileHover={{
                  y: -2,
                  boxShadow: '0 8px 25px rgba(139, 92, 246, 0.5)'
                }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                Track Events
              </motion.button>
            </motion.div>
          ) : (
            <motion.div
              key="results"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              transition={{ duration: 0.4 }}
              className="results-container"
            >
              {/* Summary cards */}
              <div className="summary-card">
                <div className="summary-item">
                  <span className="summary-label">Total Leads</span>
                  <span className="summary-value">
                    {events.events.reduce((acc, event) => acc + event.count, 0)}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Total Earnings</span>
                  <span className="summary-value earnings">
                    ₹{events.totalEarnings}
                  </span>
                </div>
              </div>

              {/* Events grid */}
              <div className="events-grid">
                {events.events.map((event, index) => (
                  <motion.div
                    key={index}
                    className={`event-card ${event.status}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="event-header">
                      {getEventIcon(event.type)}
                      <span className="event-type">
                        {event.type.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className={`event-status ${event.status}`}>
                        {event.status === 'completed' ? (
                          <FiCheck className="status-icon" />
                        ) : (
                          <FiClock className="status-icon" />
                        )}
                        {event.status}
                      </span>
                    </div>
                    <div className="event-metrics">
                      <div className="metric">
                        <span className="metric-label">Count</span>
                        <span className="metric-value">{event.count}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Value</span>
                        <span className="metric-value">₹{event.value}</span>
                      </div>
                    </div>
                    <div className="progress-bar">
                      <motion.div
                        className="progress-fill"
                        initial={{ width: 0 }}
                        animate={{
                          width: `${event.status === 'completed' ? 100 : 50}%`
                        }}
                        transition={{ duration: 0.8 }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>

              <button
                className="back-button"
                onClick={() => setEvents(null)}
              >
                Track Another VPA
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Terms section */}
        <motion.div
          className="terms-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
        >
          <h3>How tracking works</h3>
          <ul className="terms-list">
            <li>Enter your Votex VPA to view lead events</li>
            <li>Completed events show earnings you've received</li>
            <li>Pending events show potential earnings</li>
            <li>Updates in real-time as conversions happen</li>
            <li>Data refreshes automatically</li>
          </ul>
        </motion.div>
      </motion.div>

    </div>
  );
};

export default TrackLeads;